interface TransitLineFigureProps {
  size?: number;
}

/// Top-down diagram of a position fix by two crossing transits.
/// Two pairs of marks on the coast each align with the vessel,
/// drawing two lines that cross at the boat's position. Annotated
/// with the resulting fix.
export function TransitLineFigure({ size = 360 }: TransitLineFigureProps) {
  return (
    <svg
      viewBox="0 0 400 280"
      width={size}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      role="img"
      aria-label="Position fix from two crossing transits. Two pairs of charted marks aligned simultaneously fix the vessel's position where the lines cross."
    >
      {/* Sea */}
      <rect x="0" y="0" width="400" height="280" fill="#dde7ef" />
      {/* Coastline along the top + right */}
      <path
        d="M 0 0 L 400 0 L 400 280 L 380 260 L 360 250 L 340 230 L 320 215 L 300 195 L 270 165 L 240 130 L 200 95 L 160 70 L 120 50 L 80 35 L 40 20 L 0 10 Z"
        fill="#caa86b"
        stroke="#7d6a44"
        strokeWidth="0.8"
      />

      {/* Marks — front + rear for each transit */}
      {/* Transit A: church at (140, 55) + chimney at (220, 85) */}
      <Mark x={140} y={55} label="A1" sub="church" />
      <Mark x={220} y={85} label="A2" sub="chimney" />

      {/* Transit B: beacon at (315, 60) + summit at (380, 30) */}
      <Mark x={315} y={60} label="B1" sub="beacon" />
      <Mark x={380} y={30} label="B2" sub="summit" />

      {/* Vessel — where the transits cross */}
      <g transform="translate(150 200) rotate(-20)">
        <polygon
          points="0,-6 4,5 -4,5"
          fill="#1c2530"
          stroke="#0f172a"
          strokeWidth="0.6"
        />
        <line x1="0" y1="-6" x2="0" y2="-14" stroke="#0f172a" strokeWidth="1" />
      </g>

      {/* Transit lines — extend through the marks out into the sea
          past the vessel. Each is drawn dashed and labelled. */}
      <line
        x1={300}
        y1={115}
        x2={50}
        y2={235}
        stroke="#1e6091"
        strokeWidth="1.4"
        strokeDasharray="4 4"
      />
      <line
        x1={420}
        y1={-20}
        x2={50}
        y2={290}
        stroke="#c0382b"
        strokeWidth="1.4"
        strokeDasharray="4 4"
      />

      {/* Fix marker */}
      <circle cx="150" cy="200" r="6" fill="none" stroke="#0f172a" strokeWidth="2" />
      <line x1="138" y1="200" x2="162" y2="200" stroke="#0f172a" strokeWidth="0.8" />
      <line x1="150" y1="188" x2="150" y2="212" stroke="#0f172a" strokeWidth="0.8" />
      <text
        x="170"
        y="194"
        fontSize="11"
        fill="#0f172a"
        fontFamily="ui-sans-serif, system-ui"
        fontWeight="600"
      >
        Fix
      </text>
      <text
        x="170"
        y="210"
        fontSize="9"
        fill="#5d6878"
        fontFamily="ui-sans-serif, system-ui"
      >
        where the two transits cross
      </text>

      {/* Transit labels */}
      <text
        x={180}
        y={148}
        fontSize="10"
        fill="#1e6091"
        fontFamily="ui-sans-serif, system-ui"
        fontWeight="600"
        transform="rotate(-26 180 148)"
      >
        Transit A: A1 in line with A2
      </text>
      <text
        x={230}
        y={130}
        fontSize="10"
        fill="#c0382b"
        fontFamily="ui-sans-serif, system-ui"
        fontWeight="600"
        transform="rotate(-40 230 130)"
      >
        Transit B: B1 in line with B2
      </text>
    </svg>
  );
}

function Mark({
  x,
  y,
  label,
  sub,
}: {
  x: number;
  y: number;
  label: string;
  sub: string;
}) {
  return (
    <g>
      <circle cx={x} cy={y} r="3" fill="#1c2530" />
      <text
        x={x + 5}
        y={y + 3}
        fontSize="10"
        fontWeight="700"
        fill="#0f172a"
        fontFamily="ui-sans-serif, system-ui"
      >
        {label}
      </text>
      <text
        x={x + 5}
        y={y + 14}
        fontSize="9"
        fill="#5d6878"
        fontFamily="ui-sans-serif, system-ui"
      >
        {sub}
      </text>
    </g>
  );
}
